import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ProductCard } from '@/components/ProductCard';
import { getProductCardImageAlt } from '@/components/ProductCard/helpers';
import { APOLLO_CLIENTS, CustomContext } from '@/graphql/client';
import {
  type OrderAppProductVariantsBusinessDetailsQuery,
  useProductVariantsBusinessDetailsQuery,
} from '@/graphql/orderApp/api';
import type {
  ProductVariantStrapDetailsQuery,
  TaxedMoney,
} from '@/graphql/saleor/api';
import { useProductVariantStrapDetailsQuery } from '@/graphql/saleor/api';
import { useBucklesAndTools } from '@/hooks/useBucklesAndTools';
import { useRouter } from '@/hooks/useRouter';
import { useStrapSizes } from '@/hooks/useStrapSizes';
import { type StrapHit } from '@/integrations/algolia/types';
import {
  findProductCta,
  getMostRelevantProductCta,
} from '@/integrations/orderApp';
import { getMultiselectValue } from '@/lib/attributes';
import type { ProductCatalogModeCtaStyle } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { theme } from '@/theme';
import type { SizeState } from '@/views/StrapView/components';
import { AddToBagDrawer } from '@/views/StrapView/components/AddToBagDrawer';

import { useProductListCardCommon } from './helper';

interface StrapCardProps {
  cardClickHandler?: () => void;
  hit: StrapHit;
  isMostRelevantCta?: boolean;
  productCatalogModeCtaStyle?: ProductCatalogModeCtaStyle;
  sizes?: string;
  strapSizeDatas?: Record<string, any>;
  style?: React.CSSProperties;
  type?: string;
}

type ProductData = Partial<{
  businessData: OrderAppProductVariantsBusinessDetailsQuery | undefined;
  variantDetailsData: ProductVariantStrapDetailsQuery | undefined;
}>;

export const StrapCard = ({
  cardClickHandler,
  hit,
  isMostRelevantCta = false,
  productCatalogModeCtaStyle = 'DISCOVER',
  sizes,
  strapSizeDatas,
  style,
  type,
}: StrapCardProps) => {
  const {
    contextProductCatalogMode,
    detailPath,
    endProcessing,
    isLocalProcessing,
    isProcessing,
    lineAdd,
    name,
    openNotifyMe,
    query: { channel, languageCode },
    sendAlgoliaEvent,
    setContactUsModalStatus,
    setIsLocalProcessing,
    startProcessing,
  } = useProductListCardCommon(hit);
  const { push } = useRouter();
  const { formatMessage } = useIntl();
  const [data, setData] = useState<ProductData>({
    businessData: undefined,
    variantDetailsData: undefined,
  });
  const updateData = (partialData: ProductData) =>
    setData((data) => ({ ...data, ...partialData }));

  const skus = hit.variant_group;

  const { refetch: fetchVariantDetails } = useProductVariantStrapDetailsQuery({
    variables: {
      channel,
      languageCode,
      sku: hit.objectID,
    },
    skip: true,
  });
  const { refetch: fetchBusinessData } = useProductVariantsBusinessDetailsQuery(
    {
      variables: { skus, channel: channel },
      context: CustomContext({
        client: APOLLO_CLIENTS.orderApp,
        disableErrorNotification: true,
      }),
      skip: true,
    },
  );

  const [isAddToBagModalOpen, setIsAddToBagModalOpen] = useState(false);
  const [sizeState, setSizeState] = useState<SizeState>({
    selectItems: [],
    sizesSelectedItem: null,
    isLoadingOtherSize: false,
  });

  const { buckles, tools, update } = useBucklesAndTools(
    data?.variantDetailsData?.productVariant?.product.compatibleBuckles,
    null,
    true,
  );

  useStrapSizes(
    skus,
    hit?.variant_id,
    true,
    setSizeState,
    sizeState,
    strapSizeDatas,
  );

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (contextProductCatalogMode) {
      return push(detailPath);
    } else if (cta && ['ORDER', 'PRE_ORDER']?.includes(cta)) {
      setIsLocalProcessing(true);
      startProcessing();
      try {
        const { data, errors } = await fetchVariantDetails();
        if (!errors) {
          //TODO handle error
          const compatibleBucklesReferences = getMultiselectValue(
            data.productVariant?.product.compatibleBuckles,
          );
          const { data: businessData, errors: businessApiError } =
            await fetchBusinessData({
              skus: [...skus, ...compatibleBucklesReferences],
            });
          if (!businessApiError) {
            updateData({
              variantDetailsData: data,
              businessData: businessData,
            });
            const { buckles, tools } = await update(
              data?.productVariant?.product.compatibleBuckles,
            );
            if (buckles || tools) setIsAddToBagModalOpen(true);
            else {
              setIsLocalProcessing(true);
              try {
                await lineAdd(hit?.variant_id, hit?.call_to_action);
              } catch (error) {
                console.error(error);
              } finally {
                setIsLocalProcessing(false);
              }
            }
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLocalProcessing(false);
        endProcessing();
      }
    } else if (cta === 'NOTIFY') {
      if (hit.objectID) {
        openNotifyMe({ sku: hit.objectID });
      }
    } else if (cta === 'NO_CTA') {
      setContactUsModalStatus(true, hit.objectID);
    }

    sendAlgoliaEvent();
  };

  if (!hit) return null;

  const strapSku = sizeState.sizesSelectedItem
    ? (sizeState.sizesSelectedItem.data as { id: string; sku: string })?.sku
    : hit.objectID;

  // This return the CTA of the most available strap even if it's not the one displayed only on the PLPs
  const getCta = isMostRelevantCta ? getMostRelevantProductCta : findProductCta;
  const cta = getCta(
    strapSizeDatas,
    sizeState.sizesSelectedItem
      ? (sizeState.sizesSelectedItem.data as { id: string; sku: string })?.sku
      : strapSku,
  );

  return (
    <>
      <ProductCard
        backgroundColor={theme.colors.lightGrey}
        cardClickHandler={cardClickHandler}
        cta={
          (!contextProductCatalogMode ||
            productCatalogModeCtaStyle !== 'HIDDEN') && (
            <ProductCard.CTA
              cta={contextProductCatalogMode ? 'DISCOVER' : cta}
              data-test-id='StrapCardCTA'
              disabled={isProcessing}
              isProcessing={isLocalProcessing}
              onClick={handleClick}
            />
          )
        }
        ctaType='bottom'
        data-test-id='StrapCardProductCard'
        href={detailPath}
        sku={hit.objectID}
        style={style}
        topSlot={
          <ProductCard.Favorite
            data-test-id='StrapCardFavorite'
            name={name}
            price={hit.price}
            sku={hit.objectID}
            theCurrency={hit.currency}
            type={hit.product_type}
          />
        }
        type={hit.product_type}
      >
        <ProductCard.Zone>
          <ProductCard.LugSize data-test-id='StrapCardLugSize'>
            {hit.distance_between_horns}
          </ProductCard.LugSize>
          <ProductCard.Image
            alt={getProductCardImageAlt({
              formatMessage,
              name,
              sku: hit.objectID,
            })}
            className={`${
              type === 'square-strap' ? 'square-strap' : 'strap'
            } strap-card`}
            data-test-id='StrapCardImage'
            loading='lazy'
            productType='strap'
            scale={1}
            sizes={sizes ?? '(max-width: 600px) 50vw, 25vw'}
            src={hit.thumbnail_url as string}
            withBackgroundPlaceholderBackground={false}
          />
        </ProductCard.Zone>
        <ProductCard.Zone>
          <ProductCard.Sku data-test-id='StrapCardSku'>
            {hit.objectID}
          </ProductCard.Sku>
          <ProductCard.Name data-test-id='StrapCardName'>
            {name}
          </ProductCard.Name>
          <ProductCard.Price
            data-test-id='StrapCardPrice'
            price={{ amount: hit.price, currency: hit.currency }}
          />
          {hit.integrated_buckle === false && (
            <ProductCard.AdditionalInfo data-test-id='StrapCardAdditionalInfo'>
              <FormattedMessage {...commonMessages.buckleNotIncluded} />
            </ProductCard.AdditionalInfo>
          )}
        </ProductCard.Zone>
      </ProductCard>
      {data.businessData && (
        <AddToBagDrawer
          buckles={buckles}
          businessData={data.businessData}
          cta={cta}
          data-test-id='StrapCardAddToBagDrawer'
          isOpen={isAddToBagModalOpen}
          niceToHaveProducts={tools}
          setIsOpen={setIsAddToBagModalOpen}
          sizeState={sizeState}
          strap={hit}
          strapId={
            sizeState.sizesSelectedItem
              ? sizeState.sizesSelectedItem.value
              : hit?.variant_id
          }
          strapPrice={
            data.variantDetailsData?.productVariant?.pricing
              ?.price as TaxedMoney
          }
          strapSku={
            sizeState.sizesSelectedItem
              ? (
                  sizeState.sizesSelectedItem.data as {
                    id: string;
                    sku: string;
                  }
                )?.sku
              : hit?.objectID
          }
        />
      )}
    </>
  );
};
